(() => {
  if (!window.customElements || customElements.get('fr-water-trial')) return;

  const scopes = {
    home: {
      headings: '.fr-product-browser__heading h1',
      surfaces: '.fr-product-browser__heading'
    },
    story: {
      headings: '.fr-story-page__hero h1',
      surfaces: '.fr-story-page__hero'
    },
    vault: {
      headings: '.fr-vault-page__hero h1',
      surfaces: '.fr-vault-page__hero'
    }
  };
  const controls = '.fr-product-browser .fr-apparel-gallery__stage, a, button, input, select, textarea, label, summary, details, [role="button"], [role="link"], [role="textbox"], [contenteditable], [tabindex], audio, video, iframe';
  const excluded = '[data-lock-panel], fr-fortieth-reflection, .fr-fortieth';
  const allowedTextTags = new Set(['BR', 'WBR', 'EM', 'STRONG', 'SPAN', 'B', 'I', 'SMALL']);

  const copyText = (source, destination) => {
    source.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        destination.append(document.createTextNode(node.textContent));
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (allowedTextTags.has(node.tagName)) {
          const element = document.createElement(node.tagName.toLowerCase());
          copyText(node, element);
          destination.append(element);
        } else if (!node.matches('script, style, template')) {
          copyText(node, destination);
        }
      }
    });
  };

  customElements.define('fr-water-trial', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || !window.ResizeObserver || !window.AbortController) return;
      this.scope = scopes[this.dataset.scope];
      this.root = document.getElementById('MainContent');
      if (!this.scope || !this.root || getComputedStyle(this).getPropertyValue('--fr-water-trial-ready').trim() !== '1') return;
      this.controller = new AbortController();
      this.headings = new Map();
      this.surfaces = new Map();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.resizeObserver = new ResizeObserver(entries => {
        entries.forEach(({ target, contentRect }) => {
          const state = this.headings.get(target);
          if (!state || contentRect.height <= 0 || Math.abs(state.height - contentRect.height) < .25) return;
          state.height = contentRect.height;
          target.style.setProperty('--fr-water-source-height', `${contentRect.height}px`);
          target.style.setProperty('--fr-water-echo-height', `${contentRect.height * .34}px`);
          target.classList.add('fr-water-trial__heading');
        });
      });
      const { signal } = this.controller;
      this.onMotion = () => {
        if (this.motion.matches) this.cancelWater();
      };
      this.motion.addEventListener('change', this.onMotion);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.cancelWater();
      }, { signal });
      window.addEventListener('pagehide', () => this.cancelWater(), { signal });
      document.addEventListener('shopify:section:load', () => this.enhance(), { signal });
      document.addEventListener('shopify:section:unload', event => this.removeWithin(event.target), { signal });
      this.enhance();
    }

    enhance() {
      this.headings.forEach((state, heading) => {
        if (!heading.isConnected) this.removeHeading(heading, state);
      });
      this.surfaces.forEach((state, surface) => {
        if (!surface.isConnected) this.removeSurface(surface, state);
      });
      this.root.querySelectorAll(this.scope.headings).forEach(heading => {
        if (this.headings.has(heading) || heading.closest(excluded) || heading.querySelector(controls) || !heading.textContent.trim()) return;
        const reflection = document.createElement('span');
        reflection.className = 'fr-water-trial__reflection';
        reflection.setAttribute('aria-hidden', 'true');
        const echo = document.createElement('span');
        echo.className = 'fr-water-trial__echo';
        copyText(heading, echo);
        reflection.append(echo);
        heading.append(reflection);
        this.headings.set(heading, { reflection, height: -1 });
        this.resizeObserver.observe(heading);
      });
      this.root.querySelectorAll(this.scope.surfaces).forEach(surface => {
        if (this.surfaces.has(surface) || surface.closest(excluded)) return;
        const layer = document.createElement('span');
        layer.className = 'fr-water-trial__surface-layer';
        layer.setAttribute('aria-hidden', 'true');
        const sheen = document.createElement('span');
        sheen.className = 'fr-water-trial__sheen';
        layer.append(sheen);
        const rings = Array.from({ length: 3 }, () => {
          const ring = document.createElement('span');
          ring.className = 'fr-water-trial__ripple';
          layer.append(ring);
          return { element: ring, animation: null };
        });
        const controller = new AbortController();
        const state = { layer, rings, controller, next: 0, last: -Infinity };
        surface.append(layer);
        surface.addEventListener('pointerdown', event => this.touchWater(surface, state, event), { passive: true, signal: controller.signal });
        surface.addEventListener('pointercancel', () => this.cancelSurface(state), { passive: true, signal: controller.signal });
        this.surfaces.set(surface, state);
      });
    }

    touchWater(surface, state, event) {
      if (this.motion.matches || document.hidden || !event.isPrimary || event.button !== 0 || surface.closest('[hidden]')) return;
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      const control = target?.closest(controls);
      if ((control && surface.contains(control)) || target?.closest(excluded) || !window.getSelection()?.isCollapsed) return;
      const now = performance.now();
      if (now - state.last < 180) return;
      const ring = state.rings[state.next];
      if (typeof ring.element.animate !== 'function') return;
      const bounds = surface.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      state.last = now;
      state.next = (state.next + 1) % state.rings.length;
      ring.animation?.cancel();
      ring.element.style.left = `${Math.max(0, Math.min(bounds.width, event.clientX - bounds.left))}px`;
      ring.element.style.top = `${Math.max(0, Math.min(bounds.height, event.clientY - bounds.top))}px`;
      ring.animation = ring.element.animate([
        { opacity: .52, transform: 'translate(-50%, -50%) scale(.2)' },
        { opacity: 0, transform: 'translate(-50%, -50%) scale(1.5)' }
      ], { duration: 1100, easing: 'cubic-bezier(.15,.55,.3,1)' });
    }

    cancelSurface(state) {
      state.rings.forEach(ring => {
        ring.animation?.cancel();
        ring.animation = null;
      });
    }

    cancelWater() {
      this.surfaces?.forEach(state => this.cancelSurface(state));
    }

    removeHeading(heading, state) {
      this.resizeObserver.unobserve(heading);
      state.reflection.remove();
      heading.classList.remove('fr-water-trial__heading');
      heading.style.removeProperty('--fr-water-source-height');
      heading.style.removeProperty('--fr-water-echo-height');
      this.headings.delete(heading);
    }

    removeSurface(surface, state) {
      this.cancelSurface(state);
      state.controller.abort();
      state.layer.remove();
      this.surfaces.delete(surface);
    }

    removeWithin(container) {
      this.headings?.forEach((state, heading) => {
        if (container.contains(heading)) this.removeHeading(heading, state);
      });
      this.surfaces?.forEach((state, surface) => {
        if (container.contains(surface)) this.removeSurface(surface, state);
      });
    }

    disconnectedCallback() {
      this.removeWithin(this.root || this);
      this.resizeObserver?.disconnect();
      this.motion?.removeEventListener('change', this.onMotion);
      this.controller?.abort();
      this.controller = null;
    }
  });
})();
